import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  IconButton,
  AppBar,
  Toolbar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TextField,
  Switch,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import {
  CheckCircle,
  Schedule,
  LocationOn,
  Phone,
  Refresh,
  Logout,
  Map as MapIcon,
  PlayArrow,
  Stop,
  Restaurant,
  CameraAlt,
  Message,
  Groups,
  SkipNext,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { ServiceRequest, JobCompletion } from '../types';
import useCrewRoute from '../hooks/useCrewRoute';

interface JobWithCompletion extends ServiceRequest {
  completion?: JobCompletion | null;
}

// Photo thumbnail component with error handling
const PhotoThumbnail: React.FC<{ photoUrl: string; photoType: 'before' | 'after'; index: number }> = ({ photoUrl, photoType, index }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    setImageError(false);
    setImageLoading(true);
  }, [photoUrl]);
  
  return (
    <Box sx={{ position: 'relative', width: 80, height: 80, bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
      {imageLoading && !imageError && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {imageError && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 0.5, zIndex: 1 }}>
          <Typography variant="caption" color="error" align="center">
            Failed to load
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center" sx={{ fontSize: '0.6rem', mt: 0.5 }}>
            {photoUrl?.startsWith('s3://') ? 'S3 key' : photoUrl?.substring(0, 20)}
          </Typography>
        </Box>
      )}
      <img
        src={photoUrl}
        alt={`${photoType} ${index + 1}`}
        style={{ 
          width: '100%', 
          height: '100%', 
          objectFit: 'cover', 
          borderRadius: 4,
          opacity: imageError || imageLoading ? 0 : 1,
          display: 'block',
        }}
        onLoad={() => {
          setImageLoading(false);
          setImageError(false);
          console.log('[CREW-DASHBOARD] ✅ Photo loaded successfully:', {
            index,
            photoType,
            urlPrefix: photoUrl?.substring(0, 100),
          });
        }}
        onError={(e) => {
          setImageLoading(false);
          setImageError(true);
          console.error('[CREW-DASHBOARD] ❌ Photo failed to load:', {
            index,
            photoType,
            url: photoUrl,
            urlPrefix: photoUrl?.substring(0, 100),
            urlType: photoUrl?.substring(0, 20),
            isSignedUrl: photoUrl?.startsWith('https://'),
            isBase64: photoUrl?.startsWith('data:image'),
            isS3Key: photoUrl?.startsWith('s3://'),
            error: e,
          });
        }}
      />
    </Box>
  );
};

const CrewDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const {
    route,
    jobs,
    loading,
    error,
    refreshing,
    fetchRoute,
    refreshRoute,
    updateJobWithCompletion,
    updateJobCompleted,
  } = useCrewRoute();
  const [photoDialog, setPhotoDialog] = useState<{
    open: boolean;
    job: any | null;
    activeTab: 'before' | 'after';
  }>({
    open: false,
    job: null,
    activeTab: 'before'
  });
  const [smsDialog, setSmsDialog] = useState<{
    open: boolean;
    job: any | null;
    message: string;
    sending: boolean;
  }>({
    open: false,
    job: null,
    message: '',
    sending: false
  });
  const [smsSentJobs, setSmsSentJobs] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getJobId = (job: any): string =>
    (job?.jobId || job?.serviceRequestId || job?._id || '')?.toString?.() || '';

  const isOnTheWaySent = (job: any): boolean => {
    const id = getJobId(job);
    return !!(job?.onTheWaySent || (id && smsSentJobs.has(id)));
  };

  const [shiftInfo, setShiftInfo] = useState<{
    enabled: boolean;
    activeShift?: {
      id: string;
      startedAt: string;
      lunchInProgress: boolean;
      lunchTaken?: boolean;
      lunchMinutes?: number;
      jobsCompleted: number;
    } | null;
    roster?: Array<{ id: string; name: string }>;
    team?: { name: string };
    loading?: boolean;
  }>({ enabled: false });

  /** jobCompletionId -> employeeId -> on job */
  const [jobPresence, setJobPresence] = useState<Record<string, Record<string, boolean>>>({});
  const [presenceSaving, setPresenceSaving] = useState<string | null>(null);

  const roster = shiftInfo.roster ?? [];
  const crewTogglesEnabled = shiftInfo.enabled && roster.length > 0;
  const lunchInProgress = !!(shiftInfo.activeShift?.lunchInProgress);
  const lunchTaken = !!(shiftInfo.activeShift?.lunchTaken) && !lunchInProgress;
  const actionsLockedForLunch = lunchInProgress;

  const getJobCompletionId = (job: any): string | null => {
    const id = job?.completion?._id ?? job?.completion?.id;
    return id != null ? String(id) : null;
  };

  const getServiceRequestId = (job: any): string | null => {
    const id = job?.jobId ?? job?.serviceRequestId ?? job?.customerId;
    return id != null ? String(id) : null;
  };

  const defaultPresenceMap = (): Record<string, boolean> => {
    const map: Record<string, boolean> = {};
    roster.forEach((r) => {
      map[r.id] = true;
    });
    return map;
  };

  const getPresenceForJob = (job: any): Record<string, boolean> => {
    const completionId = getJobCompletionId(job);
    if (completionId && jobPresence[completionId]) {
      return jobPresence[completionId];
    }
    return defaultPresenceMap();
  };

  const buildPresencePayload = (map: Record<string, boolean>) =>
    roster.map((r) => ({
      employeeId: r.id,
      presentOnJob: map[r.id] !== false,
    }));

  const loadPresenceForJob = async (job: any) => {
    const completionId = getJobCompletionId(job);
    if (!completionId || !crewTogglesEnabled) return;

    try {
      const res = await apiService.get<{ presence: Array<{ employeeId: string; presentOnJob: boolean }> }>(
        `/crew/jobs/${completionId}/presence`
      );
      if (!res.success) return;

      const map = defaultPresenceMap();
      (res.data?.presence || []).forEach((row) => {
        if (row.employeeId) map[row.employeeId] = row.presentOnJob !== false;
      });
      setJobPresence((prev) => ({ ...prev, [completionId]: map }));
    } catch {
      // Default all on roster when no saved presence yet
      setJobPresence((prev) => ({
        ...prev,
        [completionId]: prev[completionId] ?? defaultPresenceMap(),
      }));
    }
  };

  const saveJobPresence = async (job: any, map: Record<string, boolean>) => {
    const completionId = getJobCompletionId(job);
    const serviceRequestId = getServiceRequestId(job);
    if (!completionId || !serviceRequestId) return false;

    const presentCount = Object.values(map).filter(Boolean).length;
    if (presentCount === 0) {
      alert('At least one crew member must be marked as working this job.');
      return false;
    }

    setPresenceSaving(completionId);
    try {
      const res = await apiService.put(`/crew/jobs/${completionId}/presence`, {
        serviceRequestId,
        presence: buildPresencePayload(map),
      });
      return !!res.success;
    } catch (e: any) {
      alert(e.message || 'Failed to save crew list');
      return false;
    } finally {
      setPresenceSaving(null);
    }
  };

  const setPresenceForJob = async (job: any, employeeId: string, presentOnJob: boolean) => {
    const completionId = getJobCompletionId(job);
    if (!completionId) return;

    const current = { ...getPresenceForJob(job), [employeeId]: presentOnJob };
    const presentCount = Object.values(current).filter(Boolean).length;
    if (presentCount === 0) {
      alert('At least one crew member must be marked as working this job.');
      return;
    }

    setJobPresence((prev) => ({ ...prev, [completionId]: current }));
    await saveJobPresence(job, current);
  };

  const initPresenceAfterCheckIn = (job: any, completion: any) => {
    const completionId = completion?._id ? String(completion._id) : getJobCompletionId(job);
    if (!completionId || !crewTogglesEnabled) return;
    setJobPresence((prev) => ({
      ...prev,
      [completionId]: prev[completionId] ?? defaultPresenceMap(),
    }));
  };

  const loadShift = async () => {
    try {
      const res = await apiService.get<any>('/crew/shift');
      if (res.success && res.data) {
        setShiftInfo({ ...res.data, loading: false });
      }
    } catch {
      setShiftInfo({ enabled: false, loading: false });
    }
  };

  const handleStartRoute = async () => {
    setShiftInfo((s) => ({ ...s, loading: true }));
    try {
      const res = await apiService.post('/crew/shift/start', { location: { lat: 0, lng: 0 } });
      if (res.success) {
        await loadShift();
      } else {
        alert(res.error || 'Failed to start route');
        setShiftInfo((s) => ({ ...s, loading: false }));
      }
    } catch (e: any) {
      alert(e.message || 'Failed to start route');
      setShiftInfo((s) => ({ ...s, loading: false }));
    }
  };

  const handleEndRoute = async () => {
    if (!window.confirm('End route for today?')) return;
    setShiftInfo((s) => ({ ...s, loading: true }));
    try {
      const res = await apiService.post('/crew/shift/end', { location: { lat: 0, lng: 0 } });
      if (res.success) {
        await loadShift();
        const incomplete = (res as any).incompleteCount || 0;
        if (incomplete > 0) {
          alert(
            `Route ended. ${incomplete} incomplete stop${incomplete === 1 ? '' : 's'} were sent to your provider to reschedule.`
          );
        }
      } else {
        alert(res.error || 'Failed to end route');
        setShiftInfo((s) => ({ ...s, loading: false }));
      }
    } catch (e: any) {
      alert(e.message || 'Failed to end route');
      setShiftInfo((s) => ({ ...s, loading: false }));
    }
  };

  const handleSkipJob = async (job: any) => {
    const jobId = getJobId(job);
    if (!jobId) return;
    if (
      !window.confirm(
        'Skip this visit? Customer will not be charged. Next recurring service date will be set and the customer notified.'
      )
    ) {
      return;
    }
    try {
      const res = await apiService.post(`/crew/jobs/${jobId}/skip`, {});
      if (!res.success) {
        alert(res.error || 'Failed to skip visit');
        return;
      }
      alert((res as any).message || 'Visit skipped.');
      await fetchRoute();
    } catch (e: any) {
      alert(e.message || 'Failed to skip visit');
    }
  };

  const handleLunch = async (action: 'start' | 'end') => {
    setShiftInfo((s) => ({ ...s, loading: true }));
    try {
      const res = await apiService.post(`/crew/shift/lunch/${action}`, {});
      if (res.success) {
        await loadShift();
      } else {
        alert(res.error || 'Lunch action failed');
        setShiftInfo((s) => ({ ...s, loading: false }));
      }
    } catch (e: any) {
      alert(e.message || 'Lunch action failed');
      setShiftInfo((s) => ({ ...s, loading: false }));
    }
  };

  useEffect(() => {
    if (user?.userType !== 'crew') {
      navigate('/login');
      return;
    }
    fetchRoute();
    loadShift();
  }, [user, navigate]);

  // Restore "On the way" button state from server after re-login / refresh
  useEffect(() => {
    const fromServer = new Set<string>();
    jobs.forEach((job: any) => {
      if (job?.onTheWaySent) {
        const id = getJobId(job);
        if (id) fromServer.add(id);
      }
    });
    if (fromServer.size === 0) return;
    setSmsSentJobs((prev) => {
      let changed = false;
      const next = new Set(prev);
      fromServer.forEach((id) => {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [jobs]);

  useEffect(() => {
    if (!crewTogglesEnabled) return;
    jobs.forEach((job) => {
      const completionId = getJobCompletionId(job);
      const inProgress =
        (job.checkInTime || job.completion?.checkInTime) &&
        !job.completed &&
        job.completion?.status !== 'completed';
      if (completionId && inProgress && !jobPresence[completionId]) {
        loadPresenceForJob(job);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, crewTogglesEnabled]);

  const renderCrewPresenceToggles = (job: any, compact = false) => {
    if (!crewTogglesEnabled || !getJobCompletionId(job)) return null;
    const completionId = getJobCompletionId(job)!;
    const map = getPresenceForJob(job);
    const saving = presenceSaving === completionId;

    return (
      <Box
        sx={{
          mt: compact ? 1 : 0,
          p: 1.5,
          bgcolor: 'grey.100',
          borderRadius: 1,
          opacity: saving ? 0.7 : 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
          <Groups fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight="bold">
            Crew on this job
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Turn off anyone who did not work this stop.
        </Typography>
        <FormGroup>
          {roster.map((member) => (
            <FormControlLabel
              key={member.id}
              control={
                <Switch
                  size="small"
                  checked={map[member.id] !== false}
                  disabled={saving || actionsLockedForLunch}
                  onChange={(_, checked) => setPresenceForJob(job, member.id, checked)}
                />
              }
              label={member.name}
            />
          ))}
        </FormGroup>
      </Box>
    );
  };

  const handleRefresh = () => {
    refreshRoute();
  };

  const handleLogout = () => {
    logout();
    navigate('/crew/login');
  };

  const handleCheckIn = async (job: any) => {
    try {
      // Get current location (optional, can default to job coordinates if not available)
      const location = job.coordinates || { lat: 0, lng: 0 };
      // IMPORTANT: Always use serviceRequestId or jobId (set by backend) - never use customerId
      // The backend sets both serviceRequestId and jobId to the correct ServiceRequest ID
      // job.customerId might be the customer's User ID, not the ServiceRequest ID
      const jobId = job.serviceRequestId || job.jobId;
      
      if (!jobId) {
        alert('Unable to identify job. Please refresh the page and try again.');
        console.error('[CREW-DASHBOARD] ❌ No serviceRequestId or jobId found for job:', {
          job,
          customerName: job.customerName,
          address: job.address,
          allJobKeys: Object.keys(job)
        });
        return;
      }
      
      console.log('[CREW-DASHBOARD] 🔵 Attempting check-in:', {
        jobId,
        customerName: job.customerName,
        address: job.address,
        serviceRequestId: job.serviceRequestId,
        jobIdField: job.jobId,
        customerId: job.customerId,
        hasCheckInTime: !!job.checkInTime,
        completionStatus: job.completion?.status
      });
      
      const response = await apiService.post(`/crew/jobs/${jobId}/checkin`, {
        location,
        crewSize: 1,
      });

      if (response.success) {
        console.log('[CREW-DASHBOARD] ✅ Check-in successful for:', job.customerName);
        // Update UI immediately from check-in response so Job Complete/Photos show without waiting for refetch
        const completion = (response as any).data;
        if (completion) {
          updateJobWithCompletion(String(jobId), completion);
          initPresenceAfterCheckIn(job, completion);
        }
        // Refetch in background (skip full-page loading); merge in hook preserves check-in state if API didn't return it
        fetchRoute(true).catch((err) => console.error('[CREW-DASHBOARD] Refetch after check-in failed:', err));
      } else {
        console.error('[CREW-DASHBOARD] ❌ Check-in failed:', {
          customerName: job.customerName,
          error: response.error,
          response
        });
        alert(response.error || 'Failed to check in');
      }
    } catch (err: any) {
      console.error('[CREW-DASHBOARD] ❌ Check-in exception:', {
        error: err,
        message: err.message,
        customerName: job.customerName,
        jobId: job.serviceRequestId || job.jobId
      });
      alert(err.message || 'Failed to check in. Please try again.');
    }
  };

  const [photoUploadError, setPhotoUploadError] = useState<string>('');
  const [photoUploading, setPhotoUploading] = useState(false);

  const handlePhotoUpload = async (file: File, photoType: 'before' | 'after') => {
    console.log('[CREW-DASHBOARD] handlePhotoUpload called:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      photoType,
      hasJob: !!photoDialog.job
    });
    
    if (!photoDialog.job) {
      console.error('[CREW-DASHBOARD] No job in photo dialog during upload');
      setPhotoUploadError('No job selected. Please close and reopen the photo dialog.');
      return;
    }

    setPhotoUploadError(''); // Clear previous errors
    setPhotoUploading(true); // Show loading state

    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        console.log('[CREW-DASHBOARD] FileReader onloadend triggered');
        try {
          const base64Image = reader.result as string;
          if (!base64Image) {
            setPhotoUploadError('Failed to read image file. Please try again.');
            setPhotoUploading(false);
            return;
          }

          // job.customerId is actually the ServiceRequest ID (from ScheduledRoute)
          // Also support jobId or serviceRequestId if present
          const jobId = photoDialog.job.jobId || photoDialog.job.serviceRequestId || photoDialog.job.customerId;

          if (!jobId) {
            setPhotoUploadError('Job ID not found. Please refresh and try again.');
            setPhotoUploading(false);
            return;
          }

          console.log('[CREW-DASHBOARD] Uploading photo to API...');
          // Photo uploads can take longer (base64 conversion, S3 upload, MongoDB save)
          // Use 60 second timeout for photo uploads (default is 10 seconds)
          const response = await apiService.post(`/crew/jobs/${jobId}/photos`, {
            photoType,
            photoUrl: base64Image,
          }, {
            timeout: 60000 // 60 seconds for photo uploads
          });

          if (response.success) {
            // The upload response contains the updated completion with all photos (converted to viewable URLs)
            // Use this response directly instead of refetching to avoid timing issues
            const responseData = response.data as any;
            
            console.log('[CREW-DASHBOARD] Photo upload response:', {
              hasResponseData: !!responseData,
              beforePhotosCount: responseData?.beforePhotos?.length || 0,
              afterPhotosCount: responseData?.afterPhotos?.length || 0,
              beforePhotos: responseData?.beforePhotos,
              afterPhotos: responseData?.afterPhotos,
              photoType,
            });
            
            // Extract photos from response - ensure they're arrays of strings (signed URLs)
            const updatedBeforePhotos = Array.isArray(responseData?.beforePhotos) 
              ? responseData.beforePhotos.map((photo: any) => typeof photo === 'string' ? photo : photo.url || photo)
              : [];
            const updatedAfterPhotos = Array.isArray(responseData?.afterPhotos)
              ? responseData.afterPhotos.map((photo: any) => typeof photo === 'string' ? photo : photo.url || photo)
              : [];
            
            console.log('[CREW-DASHBOARD] Extracted photos:', {
              beforePhotos: updatedBeforePhotos,
              afterPhotos: updatedAfterPhotos,
              beforeCount: updatedBeforePhotos.length,
              afterCount: updatedAfterPhotos.length,
            });
            
            // Update the job object in photoDialog with the new photos from the upload response
            const updatedJob = {
              ...photoDialog.job,
              beforePhotos: updatedBeforePhotos,
              afterPhotos: updatedAfterPhotos,
              // Also update completion object if it exists
              completion: photoDialog.job.completion ? {
                ...photoDialog.job.completion,
                beforePhotos: updatedBeforePhotos,
                afterPhotos: updatedAfterPhotos,
              } : {
                beforePhotos: updatedBeforePhotos,
                afterPhotos: updatedAfterPhotos,
              },
            };
            
            console.log('[CREW-DASHBOARD] Updated job object:', {
              jobBeforePhotos: updatedJob.beforePhotos,
              jobAfterPhotos: updatedJob.afterPhotos,
              completionBeforePhotos: updatedJob.completion?.beforePhotos,
              completionAfterPhotos: updatedJob.completion?.afterPhotos,
            });
            
            // Update photo dialog with the updated job (photos are already converted to viewable URLs by backend)
            // Use a function to ensure we're updating with the latest state
            setPhotoDialog(prev => ({
              open: true,
              job: updatedJob,
              activeTab: prev.activeTab,
            }));
            setPhotoUploadError(''); // Clear error on success
            
            // Refresh route in background (non-blocking) to keep route state in sync
            // But don't let it overwrite the photo dialog state
            setTimeout(() => {
              fetchRoute().catch(err => {
                console.error('[CREW-DASHBOARD] Error refreshing route after photo upload:', err);
                // Non-critical - photo was uploaded successfully
              });
            }, 1000); // Delay route refresh to ensure photo dialog state is set first
          } else {
            const errorMsg = response.error || response.message || 'Failed to upload photo';
            setPhotoUploadError(errorMsg);
            console.error('[CREW-DASHBOARD] Photo upload failed:', response);
          }
        } catch (uploadErr: any) {
          // Check if it's a timeout error
          const isTimeout = uploadErr.code === 'ECONNABORTED' || uploadErr.message?.includes('timeout') || uploadErr.isTimeout;
          let errorMsg = uploadErr.response?.data?.error || uploadErr.response?.data?.message || uploadErr.message || 'Failed to upload photo';
          
          if (isTimeout) {
            errorMsg = 'Photo upload timed out. The photo may be too large or your connection is slow. Please try again with a smaller photo or check your internet connection.';
          }
          
          setPhotoUploadError(errorMsg);
          console.error('[CREW-DASHBOARD] Photo upload exception:', uploadErr);
        } finally {
          setPhotoUploading(false);
        }
      };
      reader.onerror = (error) => {
        console.error('[CREW-DASHBOARD] FileReader error:', error);
        setPhotoUploadError('Failed to read image file. Please try again.');
        setPhotoUploading(false);
      };
      reader.onloadstart = () => {
        console.log('[CREW-DASHBOARD] FileReader started reading file');
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to upload photo';
      setPhotoUploadError(errorMsg);
      setPhotoUploading(false);
      console.error('[CREW-DASHBOARD] Photo upload error:', err);
    }
  };

  const handlePhotoFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    console.log('[CREW-DASHBOARD] File selected:', {
      hasFile: !!file,
      fileName: file?.name,
      fileSize: file?.size,
      fileType: file?.type,
      hasJob: !!photoDialog.job,
      activeTab: photoDialog.activeTab
    });
    
    if (!file) {
      console.warn('[CREW-DASHBOARD] No file selected');
      return;
    }
    
    if (!photoDialog.job) {
      console.error('[CREW-DASHBOARD] No job in photo dialog');
      setPhotoUploadError('No job selected. Please close and reopen the photo dialog.');
      return;
    }
    
    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    handlePhotoUpload(file, photoDialog.activeTab);
  };

  const [completeJobDialog, setCompleteJobDialog] = useState<{
    open: boolean;
    job: any | null;
    loading: boolean;
    presenceMap: Record<string, boolean>;
  }>({
    open: false,
    job: null,
    loading: false,
    presenceMap: {},
  });

  const handleCompleteJob = (job: any) => {
    if (!job) {
      console.error('[CREW-DASHBOARD] handleCompleteJob called with undefined job');
      alert('Error: Job information is missing. Please refresh the page and try again.');
      return;
    }
    setCompleteJobDialog({
      open: true,
      job,
      loading: false,
      presenceMap: getPresenceForJob(job),
    });
  };

  const confirmCompleteJob = async () => {
    const job = completeJobDialog.job;
    if (!job) {
      setCompleteJobDialog({ open: false, job: null, loading: false, presenceMap: {} });
      return;
    }

    const presenceMap = completeJobDialog.presenceMap;
    if (crewTogglesEnabled) {
      const presentCount = Object.values(presenceMap).filter(Boolean).length;
      if (presentCount === 0) {
        alert('Select at least one crew member who worked this job.');
        return;
      }
    }

    setCompleteJobDialog((prev) => ({ ...prev, loading: true }));

    try {
      const location = job.coordinates || { lat: 0, lng: 0 };
      const jobId = job.jobId || job.serviceRequestId || job.customerId;
      
      if (!jobId) {
        console.error('[CREW-DASHBOARD] No jobId found in job:', job);
        alert('Error: Could not identify job. Please refresh the page and try again.');
        setCompleteJobDialog({ open: false, job: null, loading: false, presenceMap: {} });
        return;
      }
      
      const completionId = getJobCompletionId(job);
      if (crewTogglesEnabled && completionId) {
        await saveJobPresence(job, presenceMap);
      }

      const body: Record<string, unknown> = {
        location,
        notes: '',
        issues: [],
      };
      if (crewTogglesEnabled && roster.length > 0) {
        body.presence = buildPresencePayload(presenceMap);
      }

      const response = await apiService.post(`/crew/jobs/${jobId}/complete`, body, { timeout: 60000 }) as any;

      if (response.success) {
        const completedJobId = (job.jobId || job.serviceRequestId || job.customerId)?.toString?.();
        if (completedJobId) updateJobCompleted(completedJobId);
        setCompleteJobDialog({ open: false, job: null, loading: false, presenceMap: {} });
        if (response.payment && response.payment.status === 'failed') {
          const errorMsg = response.error?.reason || response.payment.failureReason || 'Payment processing failed';
          alert(`${response.message}\n\nError: ${errorMsg}\n\nYou can retry payment processing from the provider dashboard.`);
        } else if (response.payment && response.payment.status === 'pending') {
          alert(response.message || 'Job completed successfully! Payment is pending.');
        } else {
          alert(response.message || 'Job completed successfully!');
        }
        fetchRoute(true).catch((err) => console.error('[CREW-DASHBOARD] Refetch after complete failed:', err));
        loadShift();
      } else {
        alert(response.error || 'Failed to complete job');
        setCompleteJobDialog({ open: false, job: null, loading: false, presenceMap: {} });
      }
    } catch (err: any) {
      console.error('Complete job error:', err);
      const errorMessage = err.message || 'Failed to complete job. Please try again.';
      
      if (errorMessage.includes('Network error') || errorMessage.includes('timeout')) {
        try {
          await fetchRoute();
        } catch (refreshError) {
          console.error('Error refreshing route after timeout:', refreshError);
        }
        alert('Job completion is taking longer than expected. The job may have been completed, but payment processing may still be in progress. Please refresh the page to check the job status.');
      } else {
        alert(errorMessage);
      }
      setCompleteJobDialog({ open: false, job: null, loading: false, presenceMap: {} });
    }
  };

  const getJobStatus = (job: any) => {
    // Check if job is completed first (check multiple completion indicators)
    if (job.completed || job.completion?.status === 'completed' || job.status === 'completed') {
      return { label: 'Completed', color: 'success' as const };
    }
    if (job.checkInTime) {
      return { label: 'In Progress', color: 'warning' as const };
    }
    return { label: 'Not Started', color: 'default' as const };
  };

  const formatTime = (time?: string) => {
    if (!time) return 'Not scheduled';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const completedCount = jobs.filter(j => j.completed).length;
  const totalCount = jobs.length;
  const routeComplete = totalCount > 0 && jobs.every((j) => !!j.completed);

  if (loading && !refreshing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 8, bgcolor: 'grey.50', minHeight: '100vh' }}>
      {/* Sticky header: title + jobs count + time clock (stops scroll underneath) */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          bgcolor: 'background.paper',
          boxShadow: 2,
        }}
      >
        <AppBar position="static" elevation={0} sx={{ bgcolor: 'primary.main' }}>
          <Toolbar variant="dense" sx={{ minHeight: 48, px: 1.5 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                Today&apos;s Route
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', lineHeight: 1.2 }}>
                {user?.firstName} {user?.lastName}
              </Typography>
            </Box>
            <Chip
              size="small"
              icon={<CheckCircle sx={{ fontSize: '16px !important' }} />}
              label={`${completedCount}/${totalCount}`}
              sx={{
                mr: 0.5,
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 700,
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
            <IconButton color="inherit" size="small" onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? <CircularProgress size={20} color="inherit" /> : <Refresh fontSize="small" />}
            </IconButton>
            <IconButton color="inherit" size="small" onClick={handleLogout}>
              <Logout fontSize="small" />
            </IconButton>
          </Toolbar>
        </AppBar>

        {shiftInfo.enabled && (
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              bgcolor: lunchInProgress ? 'error.50' : lunchTaken ? 'warning.50' : 'grey.100',
              borderBottom: '2px solid',
              borderColor: lunchInProgress ? 'error.main' : lunchTaken ? 'warning.main' : 'divider',
            }}
          >
            {shiftInfo.activeShift ? (
              <Stack spacing={0.75}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                    Time Clock
                  </Typography>
                  <Chip
                    size="small"
                    color={lunchInProgress ? 'error' : 'success'}
                    label={
                      lunchInProgress
                        ? 'On lunch'
                        : `Since ${new Date(shiftInfo.activeShift.startedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                    }
                    sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                  />
                  {lunchTaken && !!shiftInfo.activeShift.lunchMinutes && (
                    <Typography variant="caption" color="text.secondary">
                      Lunch {shiftInfo.activeShift.lunchMinutes}m
                    </Typography>
                  )}
                </Stack>
                {lunchInProgress ? (
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<Restaurant />}
                    onClick={() => handleLunch('end')}
                    disabled={shiftInfo.loading}
                    sx={{ fontWeight: 700, py: 0.75 }}
                  >
                    End Lunch
                  </Button>
                ) : (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Restaurant />}
                      onClick={() => handleLunch('start')}
                      disabled={shiftInfo.loading || lunchTaken}
                      sx={{ flex: 1, py: 0.75 }}
                    >
                      {lunchTaken ? 'Lunch done' : 'Start Lunch'}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      startIcon={<Stop />}
                      onClick={handleEndRoute}
                      disabled={shiftInfo.loading}
                      sx={{
                        flex: lunchTaken ? 1.35 : 1,
                        py: 0.75,
                        fontWeight: lunchTaken ? 700 : 600,
                      }}
                    >
                      End Route
                    </Button>
                  </Stack>
                )}
              </Stack>
            ) : routeComplete ? (
              <Button fullWidth size="small" variant="contained" disabled sx={{ py: 0.75 }}>
                Route complete
              </Button>
            ) : (
              <Button
                fullWidth
                size="small"
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={handleStartRoute}
                disabled={shiftInfo.loading}
                sx={{ py: 0.75 }}
              >
                Start Route
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Container maxWidth="md" sx={{ py: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Jobs List */}
        {jobs.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No jobs scheduled for today
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Check back later or contact your manager
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            {jobs.map((job, index) => {
              const status = getJobStatus(job);
              const isAdditional =
                job.serviceType?.category === 'additional_services' ||
                job.serviceType?.id === 'additionalServices' ||
                (typeof job.serviceTypeName === 'string' && job.serviceTypeName.startsWith('Additional Services'));
              
              return (
                <Card 
                  key={job.customerId || index}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 3 },
                    border: job.checkInTime ? 2 : isAdditional ? 2 : 0,
                    borderColor: job.checkInTime ? 'warning.main' : isAdditional ? '#0277bd' : 'transparent',
                    borderLeft: isAdditional ? '5px solid #0277bd' : undefined,
                    bgcolor: isAdditional ? 'rgba(2, 119, 189, 0.04)' : undefined,
                  }}
                  onClick={() => {}}
                >
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          Stop #{index + 1}: {job.customerName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(() => {
                            // Use same logic as RouteOptimizationDashboard and Today's Jobs
                            // Priority: serviceTypeName (from backend) > serviceType.category > job.services
                            
                            if (job.serviceTypeName && job.serviceTypeName !== 'Service') {
                              return job.serviceTypeName;
                            }
                            
                            // Check serviceType object (same format as RouteOptimizationDashboard)
                            if (job.serviceType?.category) {
                              const category = job.serviceType.category;
                              if (category === 'mowing') {
                                return 'Lawn Mowing';
                              } else if (category === 'fertilizing') {
                                return 'Weed Control/Fertilizing';
                              } else if (category === 'snow_removal') {
                                return 'Snow Removal';
                              } else {
                                return job.serviceType.name || 'Service';
                              }
                            }
                            
                            // Fallback to job.services (for backwards compatibility)
                            if (job.services && typeof job.services === 'object') {
                              if (job.services.category || job.services.id) {
                                const category = job.services.category || job.services.id;
                                if (category === 'mowing' || category === 'lawn_mowing' || category === 'lawnMowingTrimming') {
                                  return 'Lawn Mowing';
                                } else if (category === 'fertilizing' || category === 'weed_control' || category === 'weedControlFertilizing') {
                                  return 'Weed Control/Fertilizing';
                                } else if (category === 'snow_removal' || category === 'snowRemoval') {
                                  return 'Snow Removal';
                                }
                              }
                              // Boolean flags fallback
                              const serviceNames = [];
                              if (job.services.lawnMowingTrimming) serviceNames.push('Lawn Mowing');
                              if (job.services.weedControlFertilizing) serviceNames.push('Weed Control/Fertilizing');
                              if (job.services.snowRemoval) serviceNames.push('Snow Removal');
                              if (job.services.additionalServices) serviceNames.push('Additional Services');
                              if (serviceNames.length > 0) return serviceNames.join(', ');
                            }
                            
                            return job.serviceTypeName || 'Service';
                          })()}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {job.baggingRequired && (
                          <Chip 
                            label="Bagging Required" 
                            color="warning" 
                            size="small"
                          />
                        )}
                        <Chip 
                          label={status.label} 
                          color={status.color}
                          size="small"
                        />
                      </Stack>
                    </Stack>

                    <Divider sx={{ my: 1 }} />

                    {/* Address */}
                    <Stack direction="row" spacing={1} alignItems="flex-start" mb={1}>
                      <LocationOn sx={{ fontSize: 20, color: 'primary.main', mt: 0.5 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {job.address}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Action Buttons */}
                    <Stack
                      spacing={1}
                      sx={{
                        mt: 2,
                        opacity: actionsLockedForLunch ? 0.55 : 1,
                        pointerEvents: actionsLockedForLunch ? 'none' : 'auto',
                      }}
                    >
                      {actionsLockedForLunch && !job.completed && (
                        <Alert severity="warning" sx={{ py: 0.25 }}>
                          End Lunch above to unlock job actions
                        </Alert>
                      )}
                      {job.completed ? (
                        <Button
                          variant="outlined"
                          color="success"
                          fullWidth
                          startIcon={<CheckCircle />}
                          disabled
                        >
                          Completed
                        </Button>
                      ) : job.checkInTime ? (
                        // Job in progress - show action buttons
                        <>
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            startIcon={<CheckCircle />}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!job) {
                                console.error('[CREW-DASHBOARD] Job is undefined in onClick handler');
                                alert('Error: Job information is missing. Please refresh the page.');
                                return;
                              }
                              handleCompleteJob(job);
                            }}
                            disabled={
                              actionsLockedForLunch ||
                              job.completed ||
                              (job.completion?.status === 'completed' && job.completion?.checkInTime)
                            }
                          >
                            {job.completed || (job.completion?.status === 'completed' && job.completion?.checkInTime) ? 'Already Completed' : 'Job Complete'}
                          </Button>
                          {(job as any).frequency !== 'per-instance' &&
                            (job as any).serviceType?.category !== 'snow_removal' && (
                              <Button
                                variant="outlined"
                                color="inherit"
                                fullWidth
                                startIcon={<SkipNext />}
                                sx={{ mt: 1 }}
                                disabled={actionsLockedForLunch}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSkipJob(job);
                                }}
                              >
                                Skip Visit
                              </Button>
                            )}
                          {renderCrewPresenceToggles(job, true)}
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Message />}
                              sx={{ flex: 1 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!job.customerPhone) {
                                  alert('Customer phone number not available');
                                  return;
                                }
                                
                                // Get service name
                                const serviceName = (() => {
                                  if (job.serviceTypeName && job.serviceTypeName !== 'Service') {
                                    return job.serviceTypeName;
                                  }
                                  if (job.serviceType?.category) {
                                    const category = job.serviceType.category;
                                    if (category === 'mowing') return 'Lawn Mowing';
                                    if (category === 'fertilizing') return 'Weed Control/Fertilizing';
                                    if (category === 'snow_removal') return 'Snow Removal';
                                  }
                                  return 'your service';
                                })();
                                
                                // After clicking Arrived, message should be blank
                                const message = '';
                                
                                setSmsDialog({
                                  open: true,
                                  job,
                                  message,
                                  sending: false
                                });
                              }}
                              disabled={actionsLockedForLunch || !job.customerPhone}
                            >
                              Text Customer
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<CameraAlt />}
                              sx={{ flex: 1 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const defaultTab =
                                  job.completion?.status && job.completion.status !== 'checked_in'
                                    ? 'after'
                                    : 'before';
                                setPhotoDialog({
                                  open: true,
                                  job,
                                  activeTab: defaultTab
                                });
                              }}
                              disabled={actionsLockedForLunch}
                            >
                              Photos
                            </Button>
                          </Stack>
                        </>
                      ) : (
                        // Not started - show navigate, text customer, and arrived buttons
                        <Stack spacing={1}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Message />}
                            fullWidth
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!job.customerPhone) {
                                alert('Customer phone number not available');
                                return;
                              }
                              // Get service name
                              const serviceName = (() => {
                                if (job.serviceTypeName && job.serviceTypeName !== 'Service') {
                                  return job.serviceTypeName;
                                }
                                if (job.serviceType?.category) {
                                  const category = job.serviceType.category;
                                  if (category === 'mowing') return 'Lawn Mowing';
                                  if (category === 'fertilizing') return 'Weed Control/Fertilizing';
                                  if (category === 'snow_removal') return 'Snow Removal';
                                }
                                return 'your service';
                              })();
                              
                              // Get provider business name from route
                              const providerName = route?.providerBusinessName || 'Your service provider';
                              
                              const message = `Hi ${job.customerName?.split(' ')[0] || 'there'}. ${providerName} Crew is on the way to perform your ${serviceName}.`;
                              
                              setSmsDialog({
                                open: true,
                                job,
                                message,
                                sending: false
                              });
                            }}
                            disabled={actionsLockedForLunch || !job.customerPhone || isOnTheWaySent(job)}
                            sx={{
                              ...(isOnTheWaySent(job) ? {
                                opacity: 0.5,
                                cursor: 'not-allowed'
                              } : {})
                            }}
                          >
                            On the way
                          </Button>
                          {(job as any).frequency !== 'per-instance' &&
                            (job as any).serviceType?.category !== 'snow_removal' && (
                              <Button
                                variant="outlined"
                                color="inherit"
                                fullWidth
                                startIcon={<SkipNext />}
                                disabled={actionsLockedForLunch}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSkipJob(job);
                                }}
                              >
                                Skip Visit
                              </Button>
                            )}
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="outlined"
                              startIcon={<MapIcon />}
                              sx={{ flex: 1 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                // Use coordinates if available (more accurate), otherwise use address
                                let destination = '';
                                if (job.coordinates && job.coordinates.lat && job.coordinates.lng) {
                                  // Use coordinates for more accurate navigation
                                  destination = `${job.coordinates.lat},${job.coordinates.lng}`;
                                } else {
                                  // Fallback to address
                                  destination = encodeURIComponent(job.address);
                                }
                                
                                // Google Maps URL – dir_action=navigate starts turn-by-turn immediately
                                // when origin defaults to current location (no address input/overview)
                                const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=current+location&destination=${destination}&travelmode=driving&dir_action=navigate`;
                                window.open(mapsUrl, '_blank');
                              }}
                              disabled={actionsLockedForLunch}
                            >
                              Navigate
                            </Button>
                            <Button
                              variant="contained"
                              startIcon={<PlayArrow />}
                              sx={{ flex: 1 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCheckIn(job);
                              }}
                              disabled={actionsLockedForLunch}
                            >
                              Arrived
                            </Button>
                          </Stack>
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </Container>

      {/* Photo Upload Dialog */}
      <Dialog open={photoDialog.open} onClose={() => {
        setPhotoDialog({ open: false, job: null, activeTab: 'before' });
        setPhotoUploadError(''); // Clear error when closing
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Photos</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {photoUploadError && (
              <Alert severity="error" onClose={() => setPhotoUploadError('')}>
                {photoUploadError}
              </Alert>
            )}
            
            <Tabs value={photoDialog.activeTab} onChange={(_, newValue) => {
              setPhotoDialog({ ...photoDialog, activeTab: newValue });
              setPhotoUploadError(''); // Clear error when switching tabs
            }}>
              <Tab label="Before Photos" value="before" />
              <Tab label="After Photos" value="after" />
            </Tabs>

            <Box>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handlePhotoFileSelect}
              />
              <Button
                variant="contained"
                fullWidth
                startIcon={photoUploading ? <CircularProgress size={20} /> : <CameraAlt />}
                onClick={() => {
                  setPhotoUploadError(''); // Clear error when taking new photo
                  fileInputRef.current?.click();
                }}
                disabled={photoUploading}
                sx={{ py: 1.5 }}
              >
                {photoUploading 
                  ? 'Uploading...' 
                  : (typeof navigator !== 'undefined' && navigator.mediaDevices && 'getUserMedia' in navigator.mediaDevices
                      ? 'Take Photo with Camera'
                      : 'Select Photo')}
              </Button>
            </Box>

            {/* Display existing photos */}
            {photoDialog.job && (() => {
              // Get photos from job object (flattened) or completion object
              const photos = photoDialog.job[photoDialog.activeTab === 'before' ? 'beforePhotos' : 'afterPhotos'] 
                || photoDialog.job.completion?.[photoDialog.activeTab === 'before' ? 'beforePhotos' : 'afterPhotos']
                || [];
              // Handle both string arrays (old format) and object arrays (new format)
              const photoUrls = photos.map((photo: any) => typeof photo === 'string' ? photo : photo.url);
              
              return (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {photoDialog.activeTab === 'before' ? 'Before' : 'After'} Photos ({photoUrls.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {photoUrls.map((photoUrl: string, index: number) => (
                      <PhotoThumbnail 
                        key={index} 
                        photoUrl={photoUrl} 
                        photoType={photoDialog.activeTab}
                        index={index}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoDialog({ open: false, job: null, activeTab: 'before' })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* SMS Dialog */}
      <Dialog 
        open={smsDialog.open} 
        onClose={() => !smsDialog.sending && setSmsDialog({ open: false, job: null, message: '', sending: false })} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Send SMS to Customer</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              To: {smsDialog.job?.customerName} ({smsDialog.job?.customerPhone})
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Message"
              value={smsDialog.message}
              onChange={(e) => setSmsDialog({ ...smsDialog, message: e.target.value })}
              disabled={smsDialog.sending}
              sx={{ mt: 2 }}
              placeholder="Enter your message here..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setSmsDialog({ open: false, job: null, message: '', sending: false })}
            disabled={smsDialog.sending}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!smsDialog.message.trim() || !smsDialog.job) return;
              
              const jobId = smsDialog.job.jobId || smsDialog.job.serviceRequestId || smsDialog.job.customerId;
              if (!jobId) {
                alert('Job ID not found');
                return;
              }

              setSmsDialog({ ...smsDialog, sending: true });
              
              try {
                const response = await apiService.post(`/crew/jobs/${jobId}/send-sms`, {
                  message: smsDialog.message.trim(),
                  type: 'on_the_way',
                });

                if (response.success) {
                  // Track that SMS was sent for this job (also persisted server-side)
                  if (smsDialog.job) {
                    const sentJobId = getJobId(smsDialog.job);
                    if (sentJobId) {
                      setSmsSentJobs(prev => new Set(prev).add(sentJobId));
                    }
                  }
                  alert('SMS sent successfully!');
                  setSmsDialog({ open: false, job: null, message: '', sending: false });
                } else {
                  alert(response.error || 'Failed to send SMS');
                  setSmsDialog({ ...smsDialog, sending: false });
                }
              } catch (err: any) {
                console.error('Send SMS error:', err);
                alert(err.response?.data?.error || err.message || 'Failed to send SMS');
                setSmsDialog({ ...smsDialog, sending: false });
              }
            }}
            variant="contained"
            color="primary"
            disabled={smsDialog.sending || !smsDialog.message.trim()}
          >
            {smsDialog.sending ? 'Sending...' : 'Send SMS'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Job Confirmation Dialog */}
      <Dialog 
        open={completeJobDialog.open} 
        onClose={() => !completeJobDialog.loading && setCompleteJobDialog({ open: false, job: null, loading: false, presenceMap: {} })} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Complete Job</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph sx={{ mt: 1 }}>
            Mark this job as complete? This will process payment if applicable.
          </Typography>
          {completeJobDialog.job && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Customer: {completeJobDialog.job.customerName}
            </Typography>
          )}
          {crewTogglesEnabled && completeJobDialog.job && getJobCompletionId(completeJobDialog.job) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Crew on this job
              </Typography>
              <FormGroup>
                {roster.map((member) => (
                  <FormControlLabel
                    key={member.id}
                    control={
                      <Switch
                        checked={completeJobDialog.presenceMap[member.id] !== false}
                        disabled={completeJobDialog.loading}
                        onChange={(_, checked) => {
                          const next = {
                            ...completeJobDialog.presenceMap,
                            [member.id]: checked,
                          };
                          if (Object.values(next).filter(Boolean).length === 0) {
                            alert('At least one crew member must be marked as working this job.');
                            return;
                          }
                          setCompleteJobDialog((prev) => ({ ...prev, presenceMap: next }));
                        }}
                      />
                    }
                    label={member.name}
                  />
                ))}
              </FormGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setCompleteJobDialog({ open: false, job: null, loading: false, presenceMap: {} })}
            disabled={completeJobDialog.loading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmCompleteJob}
            variant="contained"
            color="success"
            disabled={completeJobDialog.loading}
            startIcon={completeJobDialog.loading ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            {completeJobDialog.loading ? 'Completing...' : 'Complete Job'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrewDashboard;

